import { css } from '@emotion/react';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import {
  type FC,
  useCallback, useMemo, useState,
} from 'react';
import { ButtonComponent } from '~/_shared/baseComponents/buttons/button/button.component';
import {
  DropDownItemSize, RegularDropdownComponent,
} from '~/_shared/baseComponents/dropdown';
import { InputSize } from '~/_shared/baseComponents/inputs';
import { ClientUsersDropdownContainer } from '~/_shared/components/clientUsersDropdown/clientUsersDropdown.container';
import { OverlayLoaderComponent } from '~/_shared/components/overlay/overlayLoader.component';
import { useTheme } from '~/_shared/themes/theme.hooks';
import { type ThemeProps } from '~/_shared/types/themeProps';
import { useTranslation } from '~/_shared/utils/hooks';
import { type Member } from '~/clientTeamManagement/teamManagementModal/teamManagement.types';
import { getTeamShareMapPermissionOptions } from '../teamShareMap.helpers';
import { TeamShareMapPermission } from '../teamShareMap.types';

const newMemberSectionContentStyle = ({ theme }: ThemeProps) => css({
  background: theme.modalColors.contentBackground,
  padding: '23px 22px',
  position: 'relative',
});

const newMemberFormRowStyle = css({
  display: 'flex',
  alignItems: 'space-between',
  gap: 10,
  flexDirection: 'column',
});

const buttonStyle = css({
  height: 40,
  width: 130,
  cursor: 'pointer',
});

const formSubTitleStyle = ({ theme }: ThemeProps) => css({
  color: theme.textColors.primary,
  fontSize: 16,
  lineHeight: '21px',
  marginBottom: 20,
});

const dropdownAndAddButtonContainerStyle = css({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: 10,
});

const dropdownContainerStyle = css({
  flex: '1 1 auto',
  overflow: 'hidden',
});

const permissionsDropdownContainerStyle = css({
  width: 150,
});

const dropDownLabelStyle = ({ theme }: ThemeProps) => css({
  color: theme.textColors.secondary,
  fontSize: 12,
  lineHeight: '14px',
  marginBottom: 4,
  width: '100%',
});

const addUserButtonContainerStyle = css({
  flex: '0 0 auto',
});

const loaderStyle = ({ theme }: ThemeProps) => css({
  background: theme.loadingOverlay.backgroundColor,
});

type NewMemberFormComponentProps = {
  isLoading: boolean;

  onAddUsers: (newMembers: Member[], permissionLevel: TeamShareMapPermission, onSuccess?: () => void) => void;
  excludeExistingMembersFilter: (item: Member) => boolean;
};

export const NewMemberFormComponent: FC<NewMemberFormComponentProps> = (props) => {
  const { isLoading, onAddUsers, excludeExistingMembersFilter } = props;
  const theme = useTheme();
  const [t] = useTranslation();

  const [newMembers, setNewMembers] = useState<Member[]>([]);
  const [permissionLevel, setPermissionLevel] = useState<TeamShareMapPermission>(TeamShareMapPermission.ViewOnly);

  const permissionOptions = useMemo(() => getTeamShareMapPermissionOptions(t), [t]);

  const handleAddUser = useCallback(() => {
    onAddUsers(newMembers, permissionLevel, () => {
      setNewMembers([]);
      setPermissionLevel(TeamShareMapPermission.ViewOnly);
    });
  }, [newMembers, onAddUsers, permissionLevel]);

  return (
    <div css={newMemberSectionContentStyle({ theme })}>
      {isLoading && (
        <OverlayLoaderComponent
          css={loaderStyle({ theme })}
          loaderSize={45}
        />
      )}
      <div css={formSubTitleStyle({ theme })}>
        {t('Select Team Members to Share With:')}
      </div>
      <div css={newMemberFormRowStyle}>
        <div css={dropDownLabelStyle({ theme })}>{t('Name or Email:')}</div>
        <div css={dropdownAndAddButtonContainerStyle}>
          <div css={dropdownContainerStyle}>
            <ClientUsersDropdownContainer
              onItemsChange={setNewMembers}
              excludedMembersFilter={excludeExistingMembersFilter}
              selectedItems={newMembers}
            />
          </div>
          <div css={permissionsDropdownContainerStyle}>
            <RegularDropdownComponent
              options={permissionOptions}
              inputSize={InputSize.Medium}
              itemSize={DropDownItemSize.Default}
              value={permissionLevel}
              onChange={setPermissionLevel}
            />
          </div>
          <div css={addUserButtonContainerStyle}>
            <ButtonComponent
              css={buttonStyle}
              prefixIcon={faPlus}
              onClick={handleAddUser}
              text={newMembers.length < 2 ? t('Add User') : t('Add Users')}
              isDisabled={!newMembers.length}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
